import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { useNavigate } from 'react-router-dom';

export interface JourneyStep {
  id: string;
  title: string;
  description: string;
  route: string;
  isCompleted: boolean;
  isCurrent: boolean;
  estimatedTime: string;
  order: number;
}

export const useHomebuyingJourney = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedStep, setSelectedStep] = useState<JourneyStep | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Define the critical path steps - starting with buying power calculation
  const journeySteps: JourneyStep[] = [
    {
      id: 'financial-assessment',
      title: 'Find Your Buying Power',
      description: 'Calculate your buying power and affordability',
      route: '/buying-power-enhanced',
      isCompleted: false, // TODO: Check if user has financial data
      isCurrent: false,
      estimatedTime: '15 min',
      order: 1
    },
    {
      id: 'set-preferences',
      title: 'Set Preferences',
      description: 'Set up your profile and home preferences',
      route: '/profile',
      isCompleted: false, // TODO: Check if user has profile data
      isCurrent: false,
      estimatedTime: '5 min',
      order: 2
    },
    {
      id: 'market-analysis',
      title: 'Market Analysis',
      description: 'Search and analyze properties in your area',
      route: '/smartbuy-analyzer',
      isCompleted: false, // TODO: Check if user has searched properties
      isCurrent: false,
      estimatedTime: '20 min',
      order: 3
    },
    {
      id: 'compare-options',
      title: 'Compare Options',
      description: 'Compare and evaluate your property choices',
      route: '/compare',
      isCompleted: false, // TODO: Check if user has compared properties
      isCurrent: false,
      estimatedTime: '10 min',
      order: 4
    },
    {
      id: 'final-decision',
      title: 'Make Decision',
      description: 'Review reports and finalize your choice',
      route: '/analytics',
      isCompleted: false, // TODO: Check if user has generated reports
      isCurrent: false,
      estimatedTime: '15 min',
      order: 5
    }
  ];

  // Determine current step based on user progress
  useEffect(() => {
    // Always start at step 1 - users begin with "Find Your Buying Power"
    setCurrentStep(1);
    
    // TODO: Implement logic to check user's actual progress
    // - Step 1: Check if buying power calculation is complete
    // - Step 2: Check if profile/preferences are set
    // - etc.
  }, [user]);

  // Update steps with current status
  const stepsWithStatus = journeySteps.map(step => ({
    ...step,
    isCurrent: step.order === currentStep,
    isCompleted: step.order < currentStep
  }));

  const currentStepData = stepsWithStatus.find(step => step.isCurrent);
  const nextStepData = stepsWithStatus.find(step => step.order === currentStep + 1);
  const progressPercentage = ((currentStep - 1) / journeySteps.length) * 100;

  const navigateToCurrentStep = () => {
    if (currentStepData) {
      navigate(currentStepData.route);
    }
  };

  const navigateToStep = (stepId: string) => {
    const step = stepsWithStatus.find(s => s.id === stepId);
    if (step) {
      navigate(step.route);
    }
  };

  const openStepModal = (step: JourneyStep) => {
    setSelectedStep(step);
    setIsModalOpen(true);
  };

  const closeStepModal = () => {
    setIsModalOpen(false);
    setSelectedStep(null);
  };

  return {
    steps: stepsWithStatus,
    currentStep: currentStepData,
    nextStep: nextStepData,
    progressPercentage,
    totalSteps: journeySteps.length,
    navigateToCurrentStep,
    navigateToStep,
    selectedStep,
    isModalOpen,
    openStepModal,
    closeStepModal
  };
};